import { useCallback, useEffect, useMemo, useState } from "react";
import { API_URL } from "../config/api";
import { buildGemCatalog, GEM_FILES, GEM_COLLECTION_PAGE_COUNT, getGemsForCollectionPage, isPreciousGem, PRECIOUS_GEMS_TOOLTIP, summarizeGemInventory } from "../constants/gems";
import { getPreciousGemObtainTooltip } from "../constants/chestRewards";
import { CLOVER_POTIONS, getCloverPotionById } from "../constants/cloverPotions";
import {
  GAME_POTIONS,
  POTION_COLLECTION_PAGES,
  CLOVER_POTION_CATALOG_PAGE_INDEX,
  DEMI_LUNE_POTION_ID,
} from "../constants/potions";
import { FAIRY_KEYS, KEY_COLLECTION_PAGES } from "../constants/keys";
import CloverPotionCard from "./CloverPotionCard";
import KeyCard from "./KeyCard";
import KeyExchangeModal from "./KeyExchangeModal";
import PotionCard from "./PotionCard";
import PotionBannerTitle from "./PotionBannerTitle";
import PotionExchangeModal from "./PotionExchangeModal";
import DemiLuneConfirmModal from "./DemiLuneConfirmModal";
import RewardWheelModal from "./RewardWheelModal";
import potionFrameSrc from "../assets/frame_potions.svg";
import potionArabesqueSrc from "../assets/arabesquepotion.svg";
import { canAffordKey } from "../utils/keyAffordability";
import { canAffordPotion } from "../utils/potionAffordability";
import {
  activateDemiLunePotion,
  debitDemiLunePotion,
  fetchDemiLuneStatus,
} from "../utils/questApi";
import { formatQuestJournalDate, formatQuestNumericDate } from "../utils/questDialogueText";
import {
  readPotionCatalogPage,
  writePotionCatalogPage,
} from "../utils/potionCatalogPagePreference";
import {
  readGemCatalogPage,
  writeGemCatalogPage,
} from "../utils/gemCatalogPagePreference";
import {
  readKeyCatalogPage,
  writeKeyCatalogPage,
} from "../utils/keyCatalogPagePreference";
import { markCloverRewardsAsRead } from "../utils/levelHistoryApi";
import GemBoursePanel from "./GemBoursePanel";
import "./VocabTooltip.css";
import "./GemCollectionView.css";

function CatalogSkel({ className = "" }) {
  return <span className={`gem-skel ${className}`.trim()} />;
}

function PotionCardSkeleton({ clover = false }) {
  return (
    <article
      className={`potion-card potion-card--skeleton${clover ? " potion-card--clover" : ""}`}
      aria-hidden="true"
    >
      <img
        src={potionFrameSrc}
        alt=""
        className="potion-card__frame-skel"
        aria-hidden="true"
        draggable={false}
      />
      <div className="potion-card__crest">
        <h3 className="potion-card__title">Potion</h3>
        <PotionBannerTitle name="" />
        <div className="potion-card__visual">
          <div className="potion-card__icon-wrap">
            <CatalogSkel className="potion-skel--image" />
          </div>
        </div>
      </div>

      <div className="potion-card__description-wrap">
        <p className="potion-card__description">
          Remplace un mot déjà découvert par un mot jamais encore joué.
          <span className="potion-skel-desc">
            <CatalogSkel className="potion-skel--desc-line" />
            <CatalogSkel className="potion-skel--desc-line" />
            <CatalogSkel className="potion-skel--desc-line potion-skel--desc-line-last" />
          </span>
        </p>

        <img
          src={potionArabesqueSrc}
          alt=""
          className="potion-card__arabesque"
          draggable={false}
          aria-hidden="true"
        />

        <div className="potion-card__actions">
          {clover ? (
            <div className="potion-card__clover-price-block">
              <p className="potion-card__clover-price">
                Récompense de jeu
                <CatalogSkel className="potion-skel--cost" />
              </p>
            </div>
          ) : (
            <div className="potion-card__cost-block">
              <div className="potion-card__cost">
                <p className="potion-card__cost-line potion-card__cost-line--price">
                  Prix : 1
                  <CatalogSkel className="potion-skel--cost" />
                </p>
                <p className="potion-card__cost-line potion-card__cost-line--secondary">
                  ou 00 pierres communes
                  <CatalogSkel className="potion-skel--cost" />
                </p>
              </div>
            </div>
          )}

          <button type="button" className="potion-card__obtain gem-skel" disabled tabIndex={-1}>
            {clover ? "Utiliser" : "Obtenir"}
          </button>
        </div>
      </div>

      <div className="potion-card__quantity">
        <span className="potion-card__quantity-label">
          Vous en possédez
          <CatalogSkel className="potion-skel--qty-label" />
        </span>
        <strong className="potion-card__quantity-value gem-skel">00</strong>
      </div>
    </article>
  );
}

function KeyCardSkeleton() {
  return (
    <article className="potion-card key-card key-card--skeleton">
      <img
        src={potionFrameSrc}
        alt=""
        className="potion-card__frame-skel"
        aria-hidden="true"
        draggable={false}
      />
      <div className="potion-card__crest">
        <h3 className="potion-card__title">Parchemin</h3>
        <PotionBannerTitle name="" />
        <div className="potion-card__visual">
          <div className="potion-card__icon-wrap">
            <CatalogSkel className="potion-skel--image" />
          </div>
        </div>
      </div>

      <div className="potion-card__description-wrap">
        <p className="potion-card__description">
          Permet d&apos;utiliser le pouvoir du parchemin pendant la partie en
          cours sans en consommer l&apos;usage.
          <span className="potion-skel-desc">
            <CatalogSkel className="potion-skel--desc-line" />
            <CatalogSkel className="potion-skel--desc-line" />
            <CatalogSkel className="potion-skel--desc-line potion-skel--desc-line-last" />
          </span>
        </p>

        <img
          src={potionArabesqueSrc}
          alt=""
          className="potion-card__arabesque"
          draggable={false}
          aria-hidden="true"
        />

        <div className="potion-card__actions">
          <div className="potion-card__cost-block">
            <div className="potion-card__cost">
              <p className="potion-card__cost-line potion-card__cost-line--price">
                Prix : 1
                <CatalogSkel className="potion-skel--cost" />
              </p>
              <p className="potion-card__cost-line potion-card__cost-line--secondary">
                ou 00 pierres communes
                <CatalogSkel className="potion-skel--cost" />
              </p>
            </div>
          </div>

          <button type="button" className="potion-card__obtain gem-skel" disabled tabIndex={-1}>
            Obtenir
          </button>
        </div>
      </div>

      <div className="potion-card__quantity">
        <span className="potion-card__quantity-label">
          Vous en possédez
          <CatalogSkel className="potion-skel--qty-label" />
        </span>
        <strong className="potion-card__quantity-value gem-skel">00</strong>
      </div>
    </article>
  );
}

function CollectionFooterCount({ skeleton, value, metrics = "0000" }) {
  if (!skeleton) {
    return <strong>{value}</strong>;
  }

  return (
    <strong className="potion-collection-footer__count--skel">
      <span className="potion-skel-metrics">{metrics}</span>
      <CatalogSkel className="potion-skel--footer-count" />
    </strong>
  );
}

export default function GemCollectionView({
  activeTab = "gems",
  cloverRewardsUnread = 0,
}) {
  const [gems, setGems] = useState([]);
  const [gemCounts, setGemCounts] = useState({});
  const [potionCounts, setPotionCounts] = useState({});
  const [keyCounts, setKeyCounts] = useState({});
  const [maxUnlockedLevel, setMaxUnlockedLevel] = useState(1);
  const [loading, setLoading] = useState(true);
  const [exchangePotion, setExchangePotion] = useState(null);
  const [exchangeKey, setExchangeKey] = useState(null);
  const [activeWheel, setActiveWheel] = useState(null);
  const [wheelSessionStock, setWheelSessionStock] = useState(0);
  const [demiLuneStatus, setDemiLuneStatus] = useState(null);
  const [demiLuneConfirmOpen, setDemiLuneConfirmOpen] = useState(false);
  const [demiLuneBusy, setDemiLuneBusy] = useState(false);
  const [demiLuneError, setDemiLuneError] = useState("");
  const [potionPage, setPotionPage] = useState(() =>
    readPotionCatalogPage(POTION_COLLECTION_PAGES.length)
  );
  const [gemPage, setGemPage] = useState(() =>
    readGemCatalogPage(GEM_COLLECTION_PAGE_COUNT)
  );
  const [keyPage, setKeyPage] = useState(() =>
    readKeyCatalogPage(KEY_COLLECTION_PAGES.length)
  );
  const [bourseOpen, setBourseOpen] = useState(false);

  const potionPageCount = POTION_COLLECTION_PAGES.length;
  const collectionPotions = useMemo(() => {
    const pageIds = POTION_COLLECTION_PAGES[potionPage] || [];
    return pageIds
      .map((id) => GAME_POTIONS.find((potion) => potion.id === id) ?? getCloverPotionById(id))
      .filter(Boolean);
  }, [potionPage]);
  const catalogShell = useMemo(() => buildGemCatalog(GEM_FILES, {}), []);
  const showGemSkeleton = loading && activeTab === "gems";
  const showPotionSkeleton = loading && activeTab === "potions";
  const showKeySkeleton = loading && activeTab === "keys";
  const showCatalogSkeleton = showPotionSkeleton || showKeySkeleton;
  const pageGems = useMemo(
    () => getGemsForCollectionPage(showGemSkeleton ? catalogShell : gems, gemPage),
    [showGemSkeleton, catalogShell, gems, gemPage]
  );
  const keyPageCount = KEY_COLLECTION_PAGES.length;
  const collectionKeys = useMemo(() => {
    const pageIds = KEY_COLLECTION_PAGES[keyPage] || [];
    return pageIds.map((id) => FAIRY_KEYS.find((keyItem) => keyItem.id === id)).filter(Boolean);
  }, [keyPage]);
  const title =
    activeTab === "potions"
      ? `Catalogue des potions (${GAME_POTIONS.length + CLOVER_POTIONS.length})`
      : activeTab === "keys"
        ? `Parchemins de pouvoir (${FAIRY_KEYS.length})`
        : "Collection de gemmes";

  const collectionModifier =
    activeTab === "potions"
      ? " gem-collection--potions"
      : activeTab === "keys"
        ? " gem-collection--keys"
        : "";

  const loadCollectionData = useCallback(async () => {
    let counts = {};
    let potions = {};
    let keys = {};
    let unlockedLevel = 1;
    let nextDemiLuneStatus = null;

    try {
      const [countsRes, potionsRes, keysRes, masteryRes, demiLune] = await Promise.all([
        fetch(`${API_URL}/gems/counts`),
        fetch(`${API_URL}/potions/counts`),
        fetch(`${API_URL}/keys/counts`),
        fetch(`${API_URL}/mastery/progress`),
        fetchDemiLuneStatus().catch(() => null),
      ]);

      if (countsRes.ok) {
        const json = await countsRes.json();
        counts = json.counts || {};
      }
      if (potionsRes.ok) {
        const json = await potionsRes.json();
        potions = json.counts || {};
      }
      if (keysRes.ok) {
        const json = await keysRes.json();
        keys = json.counts || {};
      }
      if (masteryRes.ok) {
        const json = await masteryRes.json();
        unlockedLevel = json.maxUnlockedLevel ?? 1;
      }
      nextDemiLuneStatus = demiLune;
      if (demiLune?.pendingForDate && demiLune.debitToken && (potions[DEMI_LUNE_POTION_ID] || 0) > 0) {
        try {
          const debit = await debitDemiLunePotion(demiLune.pendingForDate, demiLune.debitToken);
          if (debit?.potionCounts) {
            potions = debit.potionCounts;
          }
        } catch {
          // Le philtre est déjà préparé ; un nouvel essai débitera au prochain chargement.
        }
      }
    } catch {
      // Comptes à 0 tant que le backend n'est pas branché.
    }

    const catalog = buildGemCatalog(GEM_FILES, counts);

    setGems(catalog);
    setGemCounts(counts);
    setPotionCounts(potions);
    setKeyCounts(keys);
    setMaxUnlockedLevel(unlockedLevel);
    setDemiLuneStatus(nextDemiLuneStatus);
    setLoading(false);
  }, []);

  const handleConfirmDemiLune = useCallback(async () => {
    setDemiLuneBusy(true);
    setDemiLuneError("");
    try {
      const result = await activateDemiLunePotion();
      if (result?.forDate && result?.debitToken) {
        try {
          await debitDemiLunePotion(result.forDate, result.debitToken);
        } catch (debitErr) {
          setDemiLuneError(
            debitErr.message || "Le philtre est préparé, mais le débit n'a pas abouti."
          );
          await loadCollectionData();
          return;
        }
      }
      setDemiLuneConfirmOpen(false);
      await loadCollectionData();
    } catch (err) {
      setDemiLuneError(err.message || "Impossible d'utiliser le Philtre de la demi-lune.");
    } finally {
      setDemiLuneBusy(false);
    }
  }, [loadCollectionData]);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      await loadCollectionData();
      if (cancelled) return;
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [loadCollectionData]);

  useEffect(() => {
    if (activeTab !== "potions") return;
    if (potionPage !== CLOVER_POTION_CATALOG_PAGE_INDEX) return;
    if (cloverRewardsUnread <= 0) return;

    markCloverRewardsAsRead().catch((err) => {
      console.error("Erreur marquage potions de trèfles :", err);
    });
  }, [activeTab, potionPage, cloverRewardsUnread]);

  const gemInventory = useMemo(() => summarizeGemInventory(gemCounts), [gemCounts]);

  useEffect(() => {
    if (activeTab !== "gems") setBourseOpen(false);
  }, [activeTab]);

  const showBourse = activeTab === "gems" && gemPage === 1 && bourseOpen;

  const gemPageRecap =
    activeTab === "gems"
      ? gemPage === 0
        ? {
            count: gemInventory.common,
            label:
              gemInventory.common > 1 ? "pierres communes" : "pierre commune",
          }
        : {
            count: gemInventory.precious,
            label:
              gemInventory.precious > 1
                ? "pierres précieuses"
                : "pierre précieuse",
          }
      : null;

  const gemsById = useMemo(() => {
    const map = new Map();
    gems.forEach((gem) => map.set(gem.id, gem));
    return map;
  }, [gems]);

  const handleExchangeSuccess = useCallback(
    (result) => {
      if (result?.gemCounts) {
        setGemCounts(result.gemCounts);
        setGems(buildGemCatalog(GEM_FILES, result.gemCounts));
      } else {
        loadCollectionData();
      }

      if (result?.potionCounts) {
        setPotionCounts(result.potionCounts);
        const demiLuneCount = result.potionCounts[DEMI_LUNE_POTION_ID] || 0;
        setDemiLuneStatus((current) => {
          if (!current || current.pendingForDate) return current;
          if (current.unavailableReason !== "no-potion" || demiLuneCount <= 0) {
            return current;
          }
          return {
            ...current,
            potionCount: demiLuneCount,
            canActivate: true,
            unavailableReason: null,
          };
        });
        fetchDemiLuneStatus()
          .then(setDemiLuneStatus)
          .catch((err) => {
            console.error("Erreur rechargement du Philtre de la demi-lune :", err);
          });
      }

      if (result?.keyCounts) {
        setKeyCounts(result.keyCounts);
      }
    },
    [loadCollectionData]
  );

  const handlePotionPageChange = useCallback((pageIndex) => {
    setPotionPage(pageIndex);
    writePotionCatalogPage(pageIndex);
  }, []);

  const handleGemPageChange = useCallback((pageIndex) => {
    setGemPage(pageIndex);
    writeGemCatalogPage(pageIndex);
    if (pageIndex !== 1) setBourseOpen(false);
  }, []);

  const handleKeyPageChange = useCallback((pageIndex) => {
    setKeyPage(pageIndex);
    writeKeyCatalogPage(pageIndex);
  }, []);

  const renderGemCard = (gem) => {
    const isPrecious = isPreciousGem(gem.id);
    const obtainTooltip =
      !showGemSkeleton && isPrecious ? getPreciousGemObtainTooltip(gem.id) : null;
    const ownedCount = Number(gem.count) || 0;

    return (
      <article
        key={gem.id}
        className={`gem-card${
          !showGemSkeleton && ownedCount > 0 ? " gem-card--owned" : ""
        }${isPrecious ? " gem-card--precious" : ""}${
          showGemSkeleton ? " gem-card--skeleton" : ""
        }`}
      >
        <div
          className={`gem-card__visual${
            obtainTooltip ? " vocab-tooltip-wrap gem-card__visual--precious" : ""
          }`}
          tabIndex={obtainTooltip ? 0 : undefined}
          aria-hidden={showGemSkeleton ? true : undefined}
          aria-label={
            showGemSkeleton
              ? undefined
              : `${gem.label}${ownedCount > 0 ? ` — ${ownedCount}` : ""}${
                  obtainTooltip ? ` — ${obtainTooltip}` : ""
                }`
          }
        >
          <span className="gem-card__sizer" aria-hidden="true" />
          <div className="gem-card__inner">
            {showGemSkeleton ? (
              <span className="gem-card__image gem-card__image--skel">
                <span className="gem-skel gem-skel--stone" />
              </span>
            ) : (
              <img
                src={gem.imageUrl}
                alt=""
                aria-hidden="true"
                className="gem-card__image"
                decoding="sync"
              />
            )}
            <p className="gem-card__label">
              {showGemSkeleton ? (
                <span className="gem-skel gem-skel--label" />
              ) : (
                gem.label
              )}
            </p>
            <span className="gem-card__count">
              {showGemSkeleton ? (
                <span className="gem-skel gem-skel--count" />
              ) : (
                ownedCount
              )}
            </span>
            {obtainTooltip && (
              <span
                className="vocab-tooltip vocab-tooltip--wide gem-card__obtain-tooltip"
                role="tooltip"
              >
                {obtainTooltip}
              </span>
            )}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div
      className={`gem-collection${collectionModifier}${
        showCatalogSkeleton ? " gem-collection--catalog-skeleton" : ""
      }`}
      aria-busy={showGemSkeleton || showCatalogSkeleton || undefined}
    >
      <header
        className={`gem-collection-header${
          activeTab === "gems" && gemPage === 1 ? " gem-collection-header--with-bourse" : ""
        }`}
      >
        {activeTab === "gems" ? (
          <div className="gem-collection-header__title-block">
            <div className="gem-collection-header__title-row">
              <h2 className="gem-collection-title">
                {showBourse
                  ? "Bourse des pierres précieuses"
                  : gemPage === 1
                    ? "Collection de gemmes"
                    : "Collection de gemmes"}
              </h2>
              {showBourse ? (
                <button
                  type="button"
                  className="gem-bourse-close"
                  aria-label="Revenir à la collection de pierres précieuses"
                  onClick={() => setBourseOpen(false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              ) : null}
            </div>
            {gemPage === 1 && !showBourse ? (
              <button
                type="button"
                className="gem-bourse-launch gem-bourse-launch--below"
                onClick={() => setBourseOpen(true)}
              >
                Accéder à la Bourse des pierres précieuses
              </button>
            ) : null}
            {showBourse ? (
              <p className="gem-bourse-intro">
                Vous pouvez échanger des pierres précieuses contre une quantité variable de pierres communes.
              </p>
            ) : showGemSkeleton ? (
              <p className="gem-collection-title__recap gem-collection-title__recap--skeleton">
                <span className="gem-collection-title__recap-metrics">
                  {gemPage === 1 ? "(00 pierres précieuses)" : "(00 pierres communes)"}
                </span>
                <span
                  className={`gem-skel gem-skel--recap${
                    gemPage === 1 ? " gem-skel--recap-precious" : ""
                  }`}
                />
              </p>
            ) : gemPageRecap ? (
              <p className="gem-collection-title__recap">
                ({gemPageRecap.count} {gemPageRecap.label})
              </p>
            ) : null}
          </div>
        ) : (
          <h2 className="gem-collection-title">{title}</h2>
        )}
      </header>

      {activeTab === "gems" ? (
        !showGemSkeleton && gems.length === 0 ? (
          <p className="gem-collection-empty">
            Aucune pierre déclarée dans <code>src/constants/gems.js</code>.
          </p>
        ) : (
          <div className="gem-stone-collection">
            <div className="gem-collection-panel">
              {gemPage === 1 ? (
                showBourse ? (
                  <GemBoursePanel gems={pageGems} onExchanged={handleExchangeSuccess} />
                ) : (
                <div className="gem-collection-precious-layout">
                  {pageGems.map((gem) => renderGemCard(gem))}
                </div>
                )
              ) : (
                <div className="gem-collection-grid gem-collection-grid--stones">
                  {pageGems.map((gem) => renderGemCard(gem))}
                </div>
              )}
            </div>

            <div
              className="potion-collection-nav gem-collection-nav"
              role="radiogroup"
              aria-label="Pages de gemmes"
            >
              {Array.from({ length: GEM_COLLECTION_PAGE_COUNT }, (_, index) => (
                <label key={index} className="potion-collection-nav__radio">
                  <input
                    type="radio"
                    name="gem-collection-page"
                    className="potion-collection-nav__radio-input"
                    checked={gemPage === index}
                    onChange={() => handleGemPageChange(index)}
                  />
                  <span className="potion-collection-nav__radio-dot" />
                </label>
              ))}
            </div>
          </div>
        )
      ) : activeTab === "keys" ? (
        <div className="key-collection">
          <div className="key-collection-panel">
            <div
              className={`key-collection-list${
                collectionKeys.length === 1 ? " key-collection-list--solo" : ""
              }${
                collectionKeys.length === 2 ? " key-collection-list--duo" : ""
              }${
                collectionKeys.length === 3 ? " key-collection-list--trio" : ""
              }`}
            >
              {collectionKeys.map((keyItem) =>
                showKeySkeleton ? (
                  <KeyCardSkeleton key={keyItem.id} />
                ) : (
                  <KeyCard
                    key={keyItem.id}
                    keyItem={keyItem}
                    quantity={keyCounts[keyItem.id] || 0}
                    affinityGem={gemsById.get(keyItem.cost.specificGemId)}
                    canObtain={canAffordKey(keyItem, gemCounts, maxUnlockedLevel)}
                    onObtain={setExchangeKey}
                  />
                )
              )}
            </div>
          </div>

          <footer className="potion-collection-footer key-collection-footer">
            <span className="potion-collection-footer__item">
              Pierres communes disponibles&nbsp;:{" "}
              <CollectionFooterCount
                skeleton={showKeySkeleton}
                value={gemInventory.common}
              />
            </span>
            <span className="potion-collection-footer__ornament" aria-hidden="true" />
            <span className="potion-collection-footer__item">
              Pierres{" "}
              <span className="potion-collection-footer__precious-wrap vocab-tooltip-wrap">
                <span
                  className="potion-collection-footer__precious-term"
                  tabIndex={0}
                  aria-describedby="precious-gems-keys-tooltip"
                >
                  précieuses
                </span>
                <span
                  id="precious-gems-keys-tooltip"
                  className="vocab-tooltip"
                  role="tooltip"
                >
                  {PRECIOUS_GEMS_TOOLTIP}
                </span>
              </span>{" "}
              disponibles&nbsp;:{" "}
              <CollectionFooterCount
                skeleton={showKeySkeleton}
                value={gemInventory.precious}
                metrics="000"
              />
            </span>
          </footer>

          <div
            className="potion-collection-nav"
            role="radiogroup"
            aria-label="Pages de parchemins"
          >
            {Array.from({ length: keyPageCount }, (_, index) => (
              <label key={index} className="potion-collection-nav__radio">
                <input
                  type="radio"
                  name="key-collection-page"
                  className="potion-collection-nav__radio-input"
                  checked={keyPage === index}
                  onChange={() => handleKeyPageChange(index)}
                />
                <span className="potion-collection-nav__radio-dot" />
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="potion-collection">
          <div className="potion-collection-panel">
            <div
              className={`potion-collection-list${
                collectionPotions.length === 1 ? " potion-collection-list--solo" : ""
              }${
                collectionPotions.length === 2 ? " potion-collection-list--duo" : ""
              }${
                collectionPotions.length === 3 ? " potion-collection-list--trio" : ""
              }`}
            >
              {collectionPotions.map((potion) =>
                showPotionSkeleton ? (
                  <PotionCardSkeleton key={potion.id} clover={Boolean(potion.wheelType)} />
                ) : potion.wheelType ? (
                  <CloverPotionCard
                    key={potion.id}
                    potion={potion}
                    quantity={potionCounts[potion.id] || 0}
                    onUse={(selectedPotion) => {
                      setActiveWheel(selectedPotion);
                      setWheelSessionStock(potionCounts[selectedPotion.id] || 0);
                    }}
                  />
                ) : (
                  <PotionCard
                    key={potion.id}
                    potion={potion}
                    quantity={potionCounts[potion.id] || 0}
                    affinityGem={gemsById.get(potion.cost.specificGemId)}
                    canObtain={canAffordPotion(potion, gemCounts)}
                    onObtain={setExchangePotion}
                    {...(potion.id === DEMI_LUNE_POTION_ID
                      ? demiLuneStatus?.pendingForDate
                        ? {
                            activeEffectLabel: `actif sur la quête du ${formatQuestNumericDate(
                              demiLuneStatus.pendingForDate
                            )}`,
                          }
                        : {
                            canUse:
                              (potionCounts[potion.id] || 0) > 0 &&
                              Boolean(demiLuneStatus?.canActivate),
                            onUse: () => {
                              setDemiLuneError("");
                              setDemiLuneConfirmOpen(true);
                            },
                          }
                      : {})}
                  />
                )
              )}
            </div>
          </div>

          <footer className="potion-collection-footer">
            <span className="potion-collection-footer__item">
              Pierres communes disponibles&nbsp;:{" "}
              <CollectionFooterCount
                skeleton={showPotionSkeleton}
                value={gemInventory.common}
              />
            </span>
            <span className="potion-collection-footer__ornament" aria-hidden="true" />
            <span className="potion-collection-footer__item">
              Pierres{" "}
              <span className="potion-collection-footer__precious-wrap vocab-tooltip-wrap">
                <span
                  className="potion-collection-footer__precious-term"
                  tabIndex={0}
                  aria-describedby="precious-gems-tooltip"
                >
                  précieuses
                </span>
                <span
                  id="precious-gems-tooltip"
                  className="vocab-tooltip"
                  role="tooltip"
                >
                  {PRECIOUS_GEMS_TOOLTIP}
                </span>
              </span>{" "}
              disponibles&nbsp;:{" "}
              <CollectionFooterCount
                skeleton={showPotionSkeleton}
                value={gemInventory.precious}
                metrics="000"
              />
            </span>
          </footer>

          <div
            className="potion-collection-nav"
            role="radiogroup"
            aria-label="Pages de potions"
          >
            {Array.from({ length: potionPageCount }, (_, index) => (
              <label key={index} className="potion-collection-nav__radio">
                <input
                  type="radio"
                  name="potion-collection-page"
                  className="potion-collection-nav__radio-input"
                  checked={potionPage === index}
                  onChange={() => handlePotionPageChange(index)}
                />
                <span className="potion-collection-nav__radio-dot" />
              </label>
            ))}
          </div>
        </div>
      )}

      <RewardWheelModal
        open={Boolean(activeWheel)}
        wheelType={activeWheel?.wheelType}
        cloverPotionId={activeWheel?.id}
        stockQuantity={wheelSessionStock}
        onClose={() => {
          setActiveWheel(null);
          setWheelSessionStock(0);
          loadCollectionData();
        }}
        onSpinComplete={handleExchangeSuccess}
      />

      <PotionExchangeModal
        open={Boolean(exchangePotion) && activeTab === "potions"}
        potion={exchangePotion}
        gems={gems}
        gemCounts={gemCounts}
        onClose={() => setExchangePotion(null)}
        onSuccess={handleExchangeSuccess}
      />

      <DemiLuneConfirmModal
        open={demiLuneConfirmOpen}
        forDateLabel={formatQuestJournalDate(demiLuneStatus?.nextDate)}
        busy={demiLuneBusy}
        error={demiLuneError}
        onConfirm={handleConfirmDemiLune}
        onClose={() => {
          if (demiLuneBusy) return;
          setDemiLuneConfirmOpen(false);
          setDemiLuneError("");
        }}
      />

      <KeyExchangeModal
        open={Boolean(exchangeKey) && activeTab === "keys"}
        keyItem={exchangeKey}
        gems={gems}
        gemCounts={gemCounts}
        onClose={() => setExchangeKey(null)}
        onSuccess={handleExchangeSuccess}
      />

    </div>
  );
}
